import { type DashboardImgr, SUP_CAMPO, SUP_PRE, SUP_VALIDACAO, UNIDADES } from "@caint/data";
import { EixoChip, Gauge, Meter, RiskBadge, SealMedal, SealPill } from "@caint/design-system";
import type { Eixo, Momento } from "@caint/engine";
import type { PlanoAcao, SupervisaoData } from "@caint/shared";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import {
  type Finding,
  META_PRATA,
  META_SUBINDICE,
  type Severidade,
  analiseCritica,
  contarPlano,
  distConformidade,
  distRisco,
  porEixo,
  porMacro,
  porSetor,
} from "../../lib/analiseRelatorio.ts";
import { type AvaliacaoRemota, apiClient } from "../../lib/api.ts";
import { engine } from "../../lib/engine.ts";
import { conformidadeFase, diagnosticoAmostragem, indicadoresParecer } from "../../lib/supervisao.ts";
import type { ItemMerged, VisitaEvolucao } from "../../state/useAppState.ts";
import { EvolucaoVisitas } from "../EvolucaoVisitas.tsx";
import {
  AreaLinha,
  BarrasAgrupadas,
  Dispersao,
  Radar,
  Rosca,
  Velocimetro,
  faixaImgrCor,
} from "../graficos.tsx";
import { SimuladorMeta } from "./SimuladorMeta.tsx";
import "../../styles/relatorio.css";

export interface RelatorioExecutivoProps {
  items: ItemMerged[];
  momento: Momento;
  qaci: number;
  ims: number;
  avaliacaoAtual?: AvaliacaoRemota;
  /** Token da sessão — usado para puxar o Plano de Ação real (persistido) da avaliação. */
  token?: string | null;
  evolucao: VisitaEvolucao[];
  supervisao: SupervisaoData;
  carregarImgr: (cicloId: string) => Promise<DashboardImgr>;
}

/* --------------------------- helpers de UI --------------------------- */

const SEV_COR: Record<Severidade, string> = {
  ok: "var(--success)",
  atencao: "var(--warning)",
  critico: "var(--danger)",
};
const SEV_ICO: Record<Severidade, string> = { ok: "✓", atencao: "▲", critico: "⛔" };

/** Cor de banda de um score 0–1 (meta 0,46 / Prata 0,60). */
function bandaCor(score: number | null): string {
  if (score == null) return "var(--mat-na)";
  if (score >= META_PRATA) return "var(--success)";
  if (score >= META_SUBINDICE) return "var(--warning)";
  return "var(--danger)";
}
function bandaSev(score: number | null): Severidade {
  if (score == null) return "atencao";
  if (score >= META_PRATA) return "ok";
  if (score >= META_SUBINDICE) return "atencao";
  return "critico";
}

function Secao({ titulo, sub, children }: { titulo: string; sub?: string; children: ReactNode }) {
  return (
    <section className="m-card pad rel-sec">
      <div className="rel-sec-head">
        <h3>{titulo}</h3>
        {sub ? <span className="spec">{sub}</span> : null}
      </div>
      {children}
    </section>
  );
}

function Stat({ label, valor, foot, cor }: { label: string; valor: string; foot?: string; cor?: string }) {
  return (
    <div className="rel-stat">
      <span className="l">{label}</span>
      <span className="v" style={cor ? { color: cor } : undefined}>
        {valor}
      </span>
      {foot ? <span className="f">{foot}</span> : null}
    </div>
  );
}

/* Barra horizontal de score (0–1) com marca da meta em 46%. */
function BarraScore({ rotulo, sub, score }: { rotulo: string; sub?: string; score: number | null }) {
  const pct = Math.max(0, Math.min(100, (score ?? 0) * 100));
  return (
    <div className="rel-bar-row" title={sub}>
      <span className="rel-bar-label">{rotulo}</span>
      <div className="rel-bar-track">
        <div className="rel-bar-meta" />
        <div className="rel-bar-fill" style={{ width: `${pct}%`, background: bandaCor(score) }} />
      </div>
      <b className="rel-bar-val">{engine.num2(score)}</b>
    </div>
  );
}

const SHORT_MACRO: Record<string, string> = {
  G: "Governança",
  A: "Assistência",
  S: "Segurança",
  D: "Apoio Diag.",
  L: "Logística",
};
const NOME_EIXO: Record<Eixo, string> = {
  IMGO: "Gestão Operacional",
  IMQA: "Qualidade Assistencial",
  IMGR: "Gestão de Riscos",
};

/* ------------------------- seção: IMGR público ------------------------ */

function SecaoImgr({
  cicloId,
  carregar,
}: {
  cicloId?: string;
  carregar: (cicloId: string) => Promise<DashboardImgr>;
}) {
  const [dados, setDados] = useState<DashboardImgr | null>(null);
  const [estado, setEstado] = useState<"carregando" | "ok" | "vazio" | "erro">("carregando");

  useEffect(() => {
    if (!cicloId) {
      setEstado("vazio");
      return;
    }
    let vivo = true;
    setEstado("carregando");
    carregar(cicloId)
      .then((d) => {
        if (!vivo) return;
        setDados(d);
        setEstado(d.nRespondentes > 0 ? "ok" : "vazio");
      })
      .catch(() => vivo && setEstado("erro"));
    return () => {
      vivo = false;
    };
  }, [cicloId, carregar]);

  if (estado === "carregando") {
    return (
      <Secao titulo="IMGR · Percepção de riscos (questionário público)">
        <p className="spec">Carregando…</p>
      </Secao>
    );
  }
  if (estado !== "ok" || !dados) {
    return (
      <Secao titulo="IMGR · Percepção de riscos (questionário público)">
        <p className="spec">
          {estado === "erro"
            ? "Não foi possível carregar o IMGR deste ciclo."
            : "Sem respostas do questionário público neste ciclo."}
        </p>
      </Secao>
    );
  }

  const faixa = faixaImgrCor(dados.imgr);
  const eixosComp = dados.porComponentePerfil.map((c) => c.id);
  const serieGestor = dados.porComponentePerfil.map((c) => c.pctGestor ?? 0);
  const serieLiderado = dados.porComponentePerfil.map((c) => c.pctLiderado ?? 0);
  const gap = dados.porComponentePerfil.map((c) => ({
    rotulo: c.id,
    a: Math.round(c.pctGestor ?? 0),
    b: Math.round(c.pctLiderado ?? 0),
  }));

  return (
    <Secao
      titulo="IMGR · Percepção de riscos (questionário público)"
      sub={`${dados.nRespondentes} respondente(s) · ${dados.nGestores}G / ${dados.nLiderados}L`}
    >
      <div className="rel-imgr-hero">
        <Velocimetro valor={dados.imgr} cor={faixa.cor} legenda={dados.faixa ?? undefined} tamanho={168} />
        <div className="rel-stats" style={{ flex: 1 }}>
          <Stat
            label="IMGR"
            valor={dados.imgr == null ? "—" : `${Math.round(dados.imgr)}%`}
            foot={dados.faixa ?? undefined}
            cor={faixa.cor}
          />
          <Stat label="Gestores" valor={dados.pctGestor == null ? "—" : `${Math.round(dados.pctGestor)}%`} />
          <Stat
            label="Liderados"
            valor={dados.pctLiderado == null ? "—" : `${Math.round(dados.pctLiderado)}%`}
          />
        </div>
      </div>
      <h4 className="rel-h4">Percepção por componente (Gestor × Liderado)</h4>
      <Radar
        eixos={eixosComp}
        series={[
          { nome: "Gestor", cor: "var(--primary)", valores: serieGestor },
          { nome: "Liderado", cor: "var(--mat-3)", valores: serieLiderado },
        ]}
        tamanho={280}
      />
      <h4 className="rel-h4">Gap de percepção por componente</h4>
      <BarrasAgrupadas
        itens={gap}
        nomeA="Gestor"
        nomeB="Liderado"
        corA="var(--primary)"
        corB="var(--mat-3)"
      />
    </Secao>
  );
}

/* ------------------------- seção: Supervisão ------------------------- */

function SecaoSupervisao({ dados, items }: { dados: SupervisaoData; items: ItemMerged[] }) {
  const temAlgo =
    Object.keys(dados.criterios ?? {}).length > 0 ||
    (dados.amostra?.length ?? 0) > 0 ||
    dados.parecer != null;
  if (!temAlgo) {
    return (
      <Secao titulo="Supervisão AUDIN (3ª linha de defesa)">
        <p className="spec">Sem supervisão AUDIN registrada para este ciclo.</p>
      </Secao>
    );
  }
  const ind = indicadoresParecer(dados, items);
  const fases: [string, readonly { id: string; crit: string }[]][] = [
    ["Pré-visita", SUP_PRE],
    ["Campo", SUP_CAMPO],
    ["Validação", SUP_VALIDACAO],
  ];
  const diag = diagnosticoAmostragem(dados.amostra ?? []);
  const corrob = diag.real - (ind.itensRevisar ?? 0);
  const parecerRotulo =
    dados.parecer === "Confiavel"
      ? "Confiável"
      : dados.parecer === "Ressalvas"
        ? "Com ressalvas"
        : dados.parecer === "NaoConfiavel"
          ? "Não confiável"
          : "Pendente";
  const parecerSev: Severidade =
    dados.parecer === "Confiavel"
      ? "ok"
      : dados.parecer === "Ressalvas"
        ? "atencao"
        : dados.parecer === "NaoConfiavel"
          ? "critico"
          : "atencao";

  return (
    <Secao titulo="Supervisão AUDIN (3ª linha de defesa)">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <span className="spec">Parecer de confiabilidade:</span>
        <span className="rel-badge" style={{ background: SEV_COR[parecerSev] }}>
          {parecerRotulo}
        </span>
      </div>
      <div className="rel-stats">
        <Stat
          label="Corroboração"
          valor={ind.taxaCorroboracao == null ? "—" : `${Math.round(ind.taxaCorroboracao * 100)}%`}
          foot="meta ≥ 80%"
        />
        <Stat
          label="Itens a revisar"
          valor={String(ind.itensRevisar)}
          cor={ind.itensRevisar > 0 ? "var(--danger)" : undefined}
        />
        <Stat
          label="NC alta criticidade"
          valor={String(ind.metaNcAlta)}
          cor={ind.metaNcAlta > 0 ? "var(--danger)" : undefined}
        />
      </div>

      <h4 className="rel-h4">Aderência por fase da supervisão</h4>
      {fases.map(([nome, lista]) => {
        const cf = conformidadeFase(lista, dados);
        return (
          <div key={nome} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
            <span className="spec" style={{ minWidth: 92 }}>
              {nome}
            </span>
            <span style={{ flex: 1 }}>
              <Meter value={cf.indice} />
            </span>
            <b className="rel-bar-val">{engine.num2(cf.indice)}</b>
          </div>
        );
      })}

      {diag.temDados ? (
        <>
          <h4 className="rel-h4">Amostra cega (re-performance)</h4>
          <div className="rel-imgr-hero">
            <Rosca
              itens={[
                { rotulo: "Corroboram", n: Math.max(0, corrob), cor: "var(--success)" },
                { rotulo: "Divergem", n: ind.itensRevisar, cor: "var(--danger)" },
              ]}
              tamanho={132}
            />
            <div className="rel-stats" style={{ flex: 1 }}>
              <Stat label="Itens amostrados" valor={String(diag.real)} foot={`plano ${diag.plano}`} />
              <Stat label="Estratos cobertos" valor={`${diag.estratosCobertos}/3`} />
            </div>
          </div>
        </>
      ) : null}
    </Secao>
  );
}

/* --------------------------- componente raiz -------------------------- */

export function RelatorioExecutivo(props: RelatorioExecutivoProps) {
  const { items, momento, qaci, ims, avaliacaoAtual, token, evolucao, supervisao, carregarImgr } = props;
  const unidade = UNIDADES.find((u) => u.id === avaliacaoAtual?.unidadeId);

  // Todos os cálculos pesados (por setor/eixo/macro/risco/conformidade + análise
  // crítica) numa só memoização — só recomputam quando as respostas/momento mudam.
  const d = useMemo(() => {
    const calc = engine.imgq(items, momento, qaci, ims);
    const selo = engine.selo(calc.efetivo);
    const setores = porSetor(items, momento);
    const macros = porMacro(items, momento);
    const risco = distRisco(items, momento);
    return {
      calc,
      selo,
      setores,
      macros,
      risco,
      eixos: porEixo(items, momento),
      conf: distConformidade(items, momento),
      findings: analiseCritica({
        items,
        momento,
        imgq: calc,
        selo: { rotulo: selo.rotulo, nivel: selo.nivel },
        unidadeNome: unidade?.nome ?? "Unidade",
      }),
      setoresDesc: [...setores].sort((a, b) => (b.score ?? -1) - (a.score ?? -1)),
      coreCriticos: calc.core0 + calc.core1,
      // Plano de Ação = itens em maturidade 0–2 (mesma regra da página Plano de Ação).
      nPlano: contarPlano(items, momento),
      // Recorte de risco residual ≥ 8 (priorização de prazo), não o total do plano.
      nRisco: risco.plano15 + risco.plano60,
      totalCore: items.filter((it) => it.peso === 3).length,
      macroEixos: macros.map((m) => SHORT_MACRO[m.macro] ?? m.nome),
      macroValores: macros.map((m) => (m.score == null ? null : Math.round(m.score * 100))),
    };
  }, [items, momento, qaci, ims, unidade?.nome]);
  const { calc, selo, setores, eixos, conf, risco, findings, setoresDesc } = d;
  const { coreCriticos, nPlano, nRisco, totalCore, macroEixos, macroValores } = d;
  const veredito = findings[0];

  // Puxa o Plano de Ação REAL (persistido) da avaliação — mesmo total mostrado na
  // página de Plano de Ação. Sem token/avaliação (ex.: offline), cai no cálculo por
  // maturidade (`nPlano`). `null` = ainda não carregado / indisponível.
  const [planoReal, setPlanoReal] = useState<number | null>(null);
  useEffect(() => {
    const id = avaliacaoAtual?.id;
    if (!id || !token) {
      setPlanoReal(null);
      return;
    }
    let vivo = true;
    apiClient
      .planoDaAvaliacao(id, token)
      .then((p: PlanoAcao | null) => {
        if (vivo) setPlanoReal(p ? p.itens.length : null);
      })
      .catch(() => {
        if (vivo) setPlanoReal(null);
      });
    return () => {
      vivo = false;
    };
  }, [avaliacaoAtual?.id, token]);
  // Total exibido: o plano real quando disponível; senão a estimativa por maturidade.
  const planoTotal = planoReal ?? nPlano;

  const periodo = avaliacaoAtual?.ciclo?.dataInicio
    ? new Date(avaliacaoAtual.ciclo.dataInicio).toLocaleDateString("pt-BR")
    : null;
  const evolPontos = useMemo(
    () =>
      evolucao.map((v) => ({
        rotulo: v.rotulo.split("·")[0]?.trim() ?? v.rotulo,
        valor: Math.round(v.imgq * 100),
      })),
    [evolucao],
  );

  return (
    <div className="rel-cols rel-print">
      {/* 1 · Resumo executivo */}
      <section className="m-card pad rel-hero rel-span">
        <div className="rel-hero-top">
          <Gauge value={calc.efetivo} size={116} label="IMGQ" />
          <div className="rel-hero-selo">
            <SealMedal chave={selo.chave} roman={selo.romano} size={52} />
            <div style={{ marginTop: 6 }}>
              <SealPill chave={selo.chave} />
            </div>
            <div className="spec" style={{ marginTop: 4 }}>
              {selo.nivel || "Aguardando avaliação"}
            </div>
          </div>
          <div className="rel-hero-tit">
            <div className="page-eyebrow">Relatório Executivo</div>
            <h2>{unidade?.nome ?? "Unidade"}</h2>
            <div className="spec">
              {unidade?.municipio ? `${unidade.municipio}/${unidade.uf ?? ""} · ` : ""}
              {setores.length} setor(es) no escopo{periodo ? ` · ciclo iniciado em ${periodo}` : ""}
            </div>
          </div>
        </div>
        <Meter value={calc.efetivo} />
        {veredito ? (
          <p className="rel-veredito" style={{ borderColor: SEV_COR[veredito.severidade] }}>
            <b style={{ color: SEV_COR[veredito.severidade] }}>{SEV_ICO[veredito.severidade]} </b>
            {veredito.texto}
          </p>
        ) : null}
        <div className="rel-stats">
          <Stat label="IMGQ efetivo" valor={engine.pct(calc.efetivo)} cor={bandaCor(calc.efetivo)} />
          <Stat label="IMGQ bruto" valor={engine.pct(calc.bruto)} />
          <Stat
            label="CORE críticos"
            valor={String(coreCriticos)}
            foot={`de ${totalCore}`}
            cor={coreCriticos > 0 ? "var(--danger)" : undefined}
          />
          <Stat
            label="Não conformidades"
            valor={String(conf.nc)}
            cor={conf.nc > 0 ? "var(--danger)" : undefined}
          />
          <Stat
            label="Plano de Ação"
            valor={String(planoTotal)}
            foot={planoReal != null ? "itens no plano" : "maturidade 0–2"}
            cor={planoTotal > 0 ? "var(--warning)" : undefined}
          />
          <Stat
            label="Conformes"
            valor={String(conf.c + conf.cs)}
            foot={`de ${conf.totalAvaliado}`}
            cor="var(--success)"
          />
        </div>
        {calc.travado ? (
          <p className="rel-alerta">
            ⛔ Regra de exclusão CORE acionada — selo travado em ≤ 59,9% até corrigir os itens críticos.
          </p>
        ) : null}
      </section>

      {/* 2 · Subíndices por eixo */}
      <Secao titulo="Subíndices por eixo">
        {eixos.map((e) => (
          <div key={e.eixo} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0" }}>
            <EixoChip eixo={e.eixo} />
            <span style={{ flex: 1 }}>
              <Meter value={e.score} />
            </span>
            <b className="rel-bar-val">{engine.num2(e.score)}</b>
            <span className="spec" style={{ minWidth: 54, textAlign: "right" }}>
              {e.nItens} itens
            </span>
          </div>
        ))}
        <p className="spec rel-nota">Meta por eixo: 46%. Qualquer eixo abaixo trava o selo em ≤ 59,9%.</p>
      </Secao>

      {/* 2.5 · Simulador de meta (dados reais) */}
      <SimuladorMeta items={items} momento={momento} />

      {/* 3 · Maturidade por setor */}
      <Secao titulo="Maturidade por setor" sub={`${setores.length} setores · meta 46%`}>
        <div className="rel-bars">
          {setoresDesc.map((s) => (
            <BarraScore key={s.code} rotulo={s.code} sub={`${s.nome} · ${s.macroNome}`} score={s.score} />
          ))}
        </div>
        <div className="rel-table-wrap">
          <table className="rel-table">
            <thead>
              <tr>
                <th>Setor</th>
                <th className="num">Score</th>
                <th className="num">Itens</th>
                <th className="num">CORE≤1</th>
                <th className="num">NC</th>
                <th className="num">Plano</th>
              </tr>
            </thead>
            <tbody>
              {setoresDesc.map((s) => (
                <tr key={s.code}>
                  <td>
                    <span className="rel-dot" style={{ background: bandaCor(s.score) }} /> {s.code} · {s.nome}
                  </td>
                  <td className="num">{engine.num2(s.score)}</td>
                  <td className="num">{s.nItens}</td>
                  <td
                    className="num"
                    style={s.coreCriticos > 0 ? { color: "var(--danger)", fontWeight: 700 } : undefined}
                  >
                    {s.coreCriticos}
                  </td>
                  <td className="num" style={s.nc > 0 ? { color: "var(--danger)" } : undefined}>
                    {s.nc}
                  </td>
                  <td className="num" style={s.noPlano > 0 ? { color: "var(--warning)" } : undefined}>
                    {s.noPlano}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Secao>

      {/* 4 · Radar por macroprocesso */}
      <Secao titulo="Maturidade por macroprocesso">
        <Radar
          eixos={macroEixos}
          series={[{ nome: "Unidade", cor: "var(--primary)", valores: macroValores }]}
          tamanho={280}
        />
      </Secao>

      {/* 5 · Conformidade (curva ONA) */}
      <Secao titulo="Conformidade (curva ONA)" sub={`${conf.totalAvaliado} itens avaliados`}>
        <div className="rel-imgr-hero">
          <Rosca
            itens={[
              { rotulo: "NC", n: conf.nc, cor: "var(--mat-0)" },
              { rotulo: "PC", n: conf.pc, cor: "var(--mat-2)" },
              { rotulo: "C", n: conf.c, cor: "var(--mat-3)" },
              { rotulo: "CS", n: conf.cs, cor: "var(--mat-4)" },
            ]}
            tamanho={140}
          />
          <div className="rel-stats" style={{ flex: 1 }}>
            <Stat label="Conforme (C+CS)" valor={String(conf.c + conf.cs)} cor="var(--success)" />
            <Stat label="Parcial (PC)" valor={String(conf.pc)} cor="var(--warning)" />
            <Stat label="Não conforme (NC)" valor={String(conf.nc)} cor="var(--danger)" />
            <Stat label="Não aplicável" valor={String(conf.na)} />
          </div>
        </div>
        <h4 className="rel-h4">Itens CORE por nível de maturidade</h4>
        <div className="rel-core-niveis">
          {(["0", "1", "2", "3", "4"] as const).map((n) => {
            const maxN = Math.max(1, ...(["0", "1", "2", "3", "4"] as const).map((k) => conf.coreNiveis[k]));
            const altura = Math.round((conf.coreNiveis[n] / maxN) * 80); // barra relativa ao máximo (teto 80px)
            return (
              <div key={n} className="rel-core-cell" title={`${conf.coreNiveis[n]} itens CORE em nível ${n}`}>
                <div
                  className="rel-core-bar"
                  style={{ height: `${altura}px`, background: `var(--mat-${n})` }}
                />
                <b>{conf.coreNiveis[n]}</b>
                <span className="spec">N{n}</span>
              </div>
            );
          })}
        </div>
      </Secao>

      {/* 6 · Risco */}
      <Secao titulo="Risco residual" sub={`${nRisco} com risco residual ≥ 8 (priorização de prazo)`}>
        <div className="rel-imgr-hero">
          <Rosca
            itens={[
              { rotulo: "Pequeno (RP)", n: risco.rp, cor: "var(--risk-rp)" },
              { rotulo: "Moderado (RM)", n: risco.rm, cor: "var(--risk-rm)" },
              { rotulo: "Alto (RA)", n: risco.ra, cor: "var(--risk-ra)" },
              { rotulo: "Crítico (RC)", n: risco.rc, cor: "var(--risk-rc)" },
            ]}
            tamanho={140}
          />
          <div className="rel-stats" style={{ flex: 1 }}>
            <Stat
              label="Plano 15 dias"
              valor={String(risco.plano15)}
              foot="risco crítico"
              cor={risco.plano15 > 0 ? "var(--danger)" : undefined}
            />
            <Stat
              label="Plano 60 dias"
              valor={String(risco.plano60)}
              foot="risco alto"
              cor={risco.plano60 > 0 ? "var(--warning)" : undefined}
            />
          </div>
        </div>
        <h4 className="rel-h4">Inerente (P×I) × residual — o efeito da maturidade</h4>
        <Dispersao
          pontos={risco.pontos.map((p) => ({
            x: (p.inerente / 25) * 100,
            y: (p.residual / 25) * 100,
            rotulo: p.code,
          }))}
          rotuloX="Inerente (P×I)"
          rotuloY="Residual"
          cor="var(--risk-ra)"
          tamanho={280}
          rotulos={false}
          raio={4}
        />
        <p className="spec rel-nota">
          Cada ponto é um item (passe o mouse para ver o código). Abaixo da diagonal = a maturidade reduziu o
          risco.
        </p>
      </Secao>

      {/* 7 · IMGR público */}
      <SecaoImgr cicloId={avaliacaoAtual?.cicloId} carregar={carregarImgr} />

      {/* 8 · Supervisão AUDIN */}
      <SecaoSupervisao dados={supervisao} items={items} />

      {/* 9 · Evolução entre visitas */}
      {evolucao.length >= 2 ? (
        <Secao titulo="Evolução entre visitas">
          <AreaLinha pontos={evolPontos} cor="var(--primary)" altura={110} />
          <EvolucaoVisitas evolucao={evolucao} />
        </Secao>
      ) : null}

      {/* 10 · Análise crítica consolidada */}
      <Secao titulo="Análise crítica" sub="gerada automaticamente dos dados">
        <div className="rel-findings">
          {findings.map((fd: Finding) => (
            <div
              key={`${fd.dimensao}-${fd.titulo}`}
              className="rel-finding"
              style={{ borderColor: SEV_COR[fd.severidade] }}
            >
              <div className="rel-finding-head">
                <span style={{ color: SEV_COR[fd.severidade] }}>{SEV_ICO[fd.severidade]}</span>
                <span className="rel-finding-dim spec">{fd.dimensao}</span>
                <b>{fd.titulo}</b>
              </div>
              <p>{fd.texto}</p>
            </div>
          ))}
        </div>
      </Secao>
    </div>
  );
}
